import { storageService } from '@/common/services/storage/storageService';
import { useUserStore } from '@/common/stores/userStore';
import router from '@/router';
import { getDefaultRouteForUser } from '@/router/guards/authGuard';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Preferences } from '@capacitor/preferences';
import { logger } from '@/common/services/config';
import { msalInstance } from './authConfig';
import { msalCacheManager } from './msalCacheManager';

interface AuthToken {
  access_token: string;
  expires_at: string;
  user_info: {
    name: string;
    email: string;
    roles: string[];
  };
}

interface MobileAuthState {
  state: string;
  verifier: string;
}

/**
 * Unified Authentication Service
 * Simplifies authentication workflow by consolidating all auth logic
 */
class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly AUTH_ATTEMPT_KEY = 'auth_attempt';
  private readonly isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  private readonly isCapacitorApp = !!(window as any).Capacitor?.isNativePlatform?.();
  private readonly isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  private initializationPromise: Promise<void> | null = null;
  private isInitialized = false;

  /** True only for native Capacitor app running on a mobile device (not mobile web browsers) */
  private get isNativeMobileApp(): boolean {
    return this.isCapacitorApp && this.isMobile;
  }

  /**
   * Wait for initialization to complete
   */
  async waitForInitialization(): Promise<void> {
    if (this.isInitialized) return;
    if (this.initializationPromise) {
      await this.initializationPromise;
    }
  }

  /**
   * Initialize authentication - checks for existing session
   */
  async initialize(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.performInitialization();
    await this.initializationPromise;
    this.isInitialized = true;
  }

  private async performInitialization(): Promise<void> {
    logger.log('🔵 AuthService: Starting initialization...');

    try {
      // Try to restore from stored token first
      const restored = await this.restoreSession();
      if (restored) {
        logger.log('✅ AuthService: Session restored successfully');
        // Session restored - user can stay on current route
        return;
      }

      logger.log('⚠️ AuthService: No session to restore, checking MSAL...');

      // For web (desktop and mobile browsers), try MSAL silent authentication
      // Only skip for native Capacitor app on mobile device
      if (!this.isNativeMobileApp) {
        await msalInstance.initialize();

        // Handle any pending redirect response
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          await this.handleMsalResponse(response);
          // Only redirect after explicit login, not on page refresh
          if (
            router.currentRoute.value.path === '/login' ||
            router.currentRoute.value.path === '/'
          ) {
            this.redirectToHome();
          }
          return;
        }

        // Try silent token acquisition with existing accounts
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
          try {
            const tokenResponse = await msalInstance.acquireTokenSilent({
              scopes: import.meta.env.VITE_MSAL_SCOPES.split(';'),
            });
            await this.setAuthenticationState(tokenResponse.accessToken, accounts[0]);
            // Session restored silently - user stays on current route
            return;
          } catch (error) {
            logger.log('Silent auth with existing account failed, trying ssoSilent...');
          }
        }

        // Try SSO silent - uses Azure AD session cookie via hidden iframe
        // This enables cross-application SSO
        try {
          const ssoResponse = await msalInstance.ssoSilent({
            scopes: import.meta.env.VITE_MSAL_SCOPES.split(';'),
            loginHint: accounts[0]?.username,
          });
          if (ssoResponse) {
            await this.handleMsalResponse(ssoResponse);
            logger.log('✅ SSO silent authentication successful');
            return;
          }
        } catch (ssoError) {
          logger.log('SSO silent failed, user needs to login interactively');
        }
      }
    } catch (error) {
      logger.error('Auth initialization error:', error);
    }
  }

  /**
   * Sign in - handles both mobile and desktop
   */
  async signIn(email?: string): Promise<void> {
    const userStore = useUserStore();
    userStore.setLoading(true);

    try {
      // Si on est dans une app Capacitor native ET sur mobile, utiliser le flow mobile
      // Sinon, utiliser le flow desktop (même sur navigateur mobile)
      if (this.isCapacitorApp && this.isMobile) {
        logger.log('🔐 Using mobile sign-in (Capacitor app)');
        await this.mobileSignIn(email);
      } else {
        logger.log('🔐 Using desktop sign-in (web browser)');
        await this.desktopSignIn();
      }
    } catch (error) {
      logger.error('Sign in error:', error);
      throw error;
    } finally {
      userStore.setLoading(false);
    }
  }

  /**
   * Sign in with different account - forces logout first
   */
  async signInWithDifferentAccount(email?: string): Promise<void> {
    const userStore = useUserStore();
    userStore.setLoading(true);

    try {
      // Clear existing session
      await this.clearSession();

      // For web (desktop and mobile browsers), clear MSAL cache thoroughly
      if (!this.isNativeMobileApp) {
        await msalInstance.initialize();
        // Clear all MSAL cache
        msalInstance.clearCache();
      }

      if (this.isMobile && this.isIOS) {
        await this.clearIOSWebViewCache();
        await new Promise((resolve) => setTimeout(resolve, 500)); // Wait for cleanup
      }

      // Proceed with new sign in (this will force account selection)
      if (this.isCapacitorApp && this.isMobile) {
        await this.mobileSignIn(email);
      } else {
        await this.desktopSignIn(true);
      }
    } catch (error) {
      logger.error('Switch account error:', error);
      throw error;
    } finally {
      userStore.setLoading(false);
    }
  }

  /**
   * Sign out - clears all auth data and forces account selection on next login
   */
  async signOut(): Promise<void> {
    // const userStore = useUserStore();

    try {
      if (!this.isNativeMobileApp) {
        // Get account BEFORE clearing MSAL cache, so logoutRedirect can use it
        const accounts = msalInstance.getAllAccounts();
        const account = accounts.length > 0 ? accounts[0] : null;

        // Clear local storage
        await this.clearSession();

        if (account) {
          // Redirect to Azure AD logout page, which clears the server-side session
          // and redirects back to our app via postLogoutRedirectUri
          await msalInstance.logoutRedirect({ account });
          return; // logoutRedirect navigates away
        }
      } else {
        // Clear local storage first for mobile
        await this.clearSession();
        // For mobile, we need to clear any potential browser session
        // by opening a logout URL
        await this.performMobileLogout();
      }

      // Redirect to login
      router.push('/login');
    } catch (error) {
      logger.error('Sign out error:', error);
      // Even if logout fails, clear local session
      await this.clearSession();
      router.push('/login');
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const userStore = useUserStore();
    return userStore.isAuthenticated;
  }

  /**
   * Get user roles
   */
  getUserRoles(): string[] {
    const userStore = useUserStore();
    return userStore.userRoles;
  }

  /**
   * Get access token (with automatic refresh if needed)
   * @param forceRefresh - If true, skip expiry check and force a token refresh via MSAL
   */
  async getAccessToken(forceRefresh = false): Promise<string | null> {
    const userStore = useUserStore();

    if (!userStore.isAuthenticated) {
      return null;
    }

    // Check if token is still valid
    const tokenData = storageService.getItem(this.TOKEN_KEY);
    if (!tokenData) {
      return null;
    }

    try {
      const authToken: AuthToken = JSON.parse(tokenData);

      // Check if token is expired (with 5 minute buffer) or force refresh requested
      const expiresAt = new Date(authToken.expires_at);
      const now = new Date();
      const bufferTime = 5 * 60 * 1000; // 5 minutes
      const needsRefresh = forceRefresh || now.getTime() >= expiresAt.getTime() - bufferTime;

      if (needsRefresh) {
        logger.log(
          forceRefresh
            ? '🔄 Forced token refresh requested...'
            : 'Token expired, attempting refresh...'
        );

        // Use MSAL silent acquisition for token refresh (web: desktop + mobile browsers)
        if (!this.isNativeMobileApp) {
          logger.log('🔄 Using MSAL silent acquisition for browser app');
          const refreshed = await this.refreshTokenViaMsal();

          if (refreshed) {
            const newTokenData = storageService.getItem(this.TOKEN_KEY);
            if (newTokenData) {
              const newAuthToken: AuthToken = JSON.parse(newTokenData);
              return newAuthToken.access_token;
            }
          }
        }

        // If refresh failed, clear session
        await this.clearSession();
        return null;
      }

      return authToken.access_token;
    } catch (error) {
      logger.error('Error getting access token:', error);
      return null;
    }
  }

  // Private methods
  private async performMobileLogout(): Promise<void> {
    try {
      // For mobile, open Microsoft logout URL to clear server-side session
      const logoutUrl = `https://login.microsoftonline.com/organizations/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin + '/login')}`;

      await Browser.open({
        url: logoutUrl,
        windowName: '_blank',
        ...(this.isIOS && {
          presentationStyle: 'popover',
          toolbarColor: '#ffffff',
        }),
      });

      // Close the browser after a short delay to allow logout to complete
      setTimeout(async () => {
        await Browser.close();
      }, 2000);
    } catch (error) {
      logger.warn('Mobile logout error:', error);
    }
  }

  private async mobileSignIn(email?: string): Promise<void> {
    logger.log('🔐 Starting mobile sign-in process');
    const authUrl = await this.buildMobileAuthUrl(email);
    logger.log('🔗 Auth URL generated:', authUrl);

    // Open browser for authentication
    logger.log('🌐 Opening browser for authentication');
    await Browser.open({
      url: authUrl,
      windowName: '_blank',
      ...(this.isIOS && {
        presentationStyle: 'popover',
        toolbarColor: '#ffffff',
      }),
    });

    // Listen for app URL open (redirect back)
    logger.log('👂 Setting up app URL listener');
    App.addListener('appUrlOpen', async (event) => {
      logger.log('📱 App URL opened:', event.url);
      await Browser.close();
      await this.handleMobileRedirect(event.url);
    });
  }

  private async desktopSignIn(forceAccountSelection = false): Promise<void> {
    await msalInstance.initialize();

    const scopes = import.meta.env.VITE_MSAL_SCOPES.split(';');

    // If not forcing account selection, try SSO silent first
    if (!forceAccountSelection) {
      try {
        const ssoResponse = await msalInstance.ssoSilent({ scopes });
        if (ssoResponse) {
          await this.handleMsalResponse(ssoResponse);
          this.redirectToHome();
          return;
        }
      } catch (ssoError) {
        logger.log('SSO silent failed during sign-in, falling back to redirect');
      }
    }

    // Interactive redirect login (navigates the whole browser like the old app)
    // The response will be handled by handleRedirectPromise() in performInitialization()
    await msalInstance.loginRedirect({
      scopes: scopes,
      ...(forceAccountSelection && { prompt: 'select_account' }),
    });
  }

  private async buildMobileAuthUrl(email?: string): Promise<string> {
    // Generate state and verifier for PKCE
    const state = this.generateRandomString(20);
    const verifier = this.generateRandomString(43);
    const challenge = await this.generateCodeChallenge(verifier);

    // Store auth attempt for validation
    const authAttempt: MobileAuthState = { state, verifier };
    await Preferences.set({
      key: this.AUTH_ATTEMPT_KEY,
      value: JSON.stringify(authAttempt),
    });

    const params = new URLSearchParams({
      client_id: import.meta.env.VITE_MSAL_CLIENT_ID,
      response_type: 'code',
      redirect_uri: import.meta.env.VITE_MSAL_MOBILE_REDIRECT_URI,
      scope: import.meta.env.VITE_MSAL_SCOPES.replace(/;/g, ' '), // Convert semicolon to space for URL
      response_mode: 'query',
      state: state,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      prompt: 'select_account',
      domain_hint: 'em-normandie.fr',
      ...(email && { login_hint: email }),
    });

    const authUrl = `https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize?${params.toString()}`;
    logger.log('Mobile auth URL scopes:', params.get('scope'));

    return authUrl;
  }

  private async handleMobileRedirect(url: string): Promise<void> {
    try {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);

      // Check for errors
      const error = params.get('error');
      if (error) {
        throw new Error(`Authentication error: ${error}`);
      }

      // Get authorization code
      const code = params.get('code');
      const state = params.get('state');

      if (!code || !state) {
        throw new Error('Missing authorization code or state');
      }

      // Validate state
      const storedAttempt = await Preferences.get({
        key: this.AUTH_ATTEMPT_KEY,
      });
      if (!storedAttempt.value) {
        throw new Error('No stored auth attempt found');
      }

      const authAttempt: MobileAuthState = JSON.parse(storedAttempt.value);
      if (authAttempt.state !== state) {
        throw new Error('State mismatch - possible CSRF attack');
      }

      // Exchange code for token
      const tokenResponse = await this.exchangeCodeForToken(code, authAttempt.verifier);
      await this.setAuthenticationState(tokenResponse.access_token, null, tokenResponse);

      // Clean up
      await Preferences.remove({ key: this.AUTH_ATTEMPT_KEY });

      this.redirectToHome();
    } catch (error) {
      logger.error('Mobile redirect handling error:', error);
      // router.push('/login?error=auth_failed');
    }
  }

  private async exchangeCodeForToken(code: string, verifier: string): Promise<any> {
    const params = new URLSearchParams({
      client_id: import.meta.env.VITE_MSAL_CLIENT_ID,
      scope: import.meta.env.VITE_MSAL_SCOPES.replace(/;/g, ' '), // Convert semicolon to space
      code: code,
      redirect_uri: import.meta.env.VITE_MSAL_MOBILE_REDIRECT_URI,
      grant_type: 'authorization_code',
      code_verifier: verifier,
    });

    const response = await fetch(
      'https://login.microsoftonline.com/organizations/oauth2/v2.0/token',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Token exchange failed:', response.status, errorText);
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    const tokenData = await response.json();
    logger.log('Token exchange response:', {
      has_access_token: !!tokenData.access_token,
      has_refresh_token: !!tokenData.refresh_token,
      expires_in: tokenData.expires_in,
      scope: tokenData.scope,
    });

    return tokenData;
  }

  private async handleMsalResponse(response: any): Promise<void> {
    const userStore = useUserStore();
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);

      // For MSAL responses, create a token response object
      // Note: MSAL in browser typically doesn't expose refresh tokens directly for security
      const tokenResponse = {
        access_token: response.accessToken,
        refresh_token: null, // MSAL handles refresh internally
        expires_in: response.expiresOn
          ? Math.floor((response.expiresOn.getTime() - Date.now()) / 1000)
          : 3600,
      };
      logger.log('Silent auth successful via MSAL', response.accessToken);

      await this.setAuthenticationState(response.accessToken, accounts[0], tokenResponse);
    }
  }

  private async setAuthenticationState(
    accessToken: string,
    account?: any,
    tokenResponse?: any
  ): Promise<void> {
    const userStore = useUserStore();
    // const tokenData = this.decodeToken(accessToken);

    // Extract user info
    const userInfo = {
      name: account?.name || '',
      email: account?.username || '',
      roles: account?.idTokenClaims?.roles || [],
    };

    // Create auth token object
    const authToken: AuthToken = {
      access_token: accessToken,
      expires_at: new Date(Date.now() + (tokenResponse?.expires_in || 3600) * 1000).toISOString(),
      user_info: userInfo,
    };

    // Log token storage info
    logger.log('💾 Storing auth token:', {
      platform: this.isMobile ? 'mobile' : 'desktop',
      refresh_strategy: 'msal_silent',
    });

    // Store token
    storageService.setItem(this.TOKEN_KEY, JSON.stringify(authToken));

    // Update user store
    userStore.setIsAuthenticated(true);
    userStore.setAccessToken(accessToken);
    userStore.setUserName(userInfo.name);
    userStore.setUserEmail(userInfo.email);
    userStore.setUserRoles(userInfo.roles);

    if (account) {
      userStore.setAccount(account);
    }
  }

  private async restoreSession(): Promise<boolean> {
    logger.log('🔍 Attempting to restore session...');
    try {
      const tokenData = storageService.getItem(this.TOKEN_KEY);
      if (!tokenData) {
        logger.log('❌ No token found in storage');
        return false;
      }

      logger.log('📦 Token found in storage, parsing...');
      const authToken: AuthToken = JSON.parse(tokenData);

      // Check if token is expired
      const expiresAt = new Date(authToken.expires_at);
      const now = new Date();
      logger.log('⏰ Token expiry check:', {
        expiresAt: expiresAt.toISOString(),
        now: now.toISOString(),
        isExpired: now >= expiresAt,
      });

      if (now >= expiresAt) {
        logger.log('⌛ Token expired, attempting refresh...');
        // Use MSAL silent acquisition for token refresh (web: desktop + mobile browsers)
        if (!this.isNativeMobileApp) {
          logger.log('🔄 Refreshing via MSAL silent acquisition (browser)');
          const refreshed = await this.refreshTokenViaMsal();
          if (refreshed) {
            logger.log('✅ Token refreshed successfully');
            return true;
          }
        }

        // Token expired and couldn't refresh
        logger.log('❌ Token expired and refresh failed');
        storageService.removeItem(this.TOKEN_KEY);
        return false;
      }

      logger.log('✅ Token is valid, restoring user state...');
      // Restore user state
      const userStore = useUserStore();

      userStore.setIsAuthenticated(true);
      userStore.setAccessToken(authToken.access_token);
      userStore.setUserName(authToken.user_info.name);
      userStore.setUserEmail(authToken.user_info.email);
      userStore.setUserRoles(authToken.user_info.roles);

      logger.log('✅ Session restored successfully', {
        userName: authToken.user_info.name,
        roles: authToken.user_info.roles,
      });
      return true;
    } catch (error) {
      logger.error('❌ Session restoration error:', error);
      storageService.removeItem(this.TOKEN_KEY);
      return false;
    }
  }

  private async refreshTokenViaMsal(): Promise<boolean> {
    try {
      await msalInstance.initialize();
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length === 0) {
        logger.warn('No MSAL accounts available for silent refresh');
        return false;
      }

      const silentRequest = {
        scopes: import.meta.env.VITE_MSAL_SCOPES.split(';'),
        account: accounts[0],
        forceRefresh: true, // Force refresh to get new token
      };

      const response = await msalInstance.acquireTokenSilent(silentRequest);

      // Create token response object
      const tokenResponse = {
        access_token: response.accessToken,
        refresh_token: null, // MSAL handles refresh internally
        expires_in: response.expiresOn
          ? Math.floor((response.expiresOn.getTime() - Date.now()) / 1000)
          : 3600,
      };

      await this.setAuthenticationState(response.accessToken, accounts[0], tokenResponse);
      logger.log('Token refreshed successfully via MSAL');

      return true;
    } catch (error) {
      logger.error('MSAL silent token refresh error:', error);
      return false;
    }
  }

  private async clearSession(): Promise<void> {
    const userStore = useUserStore();

    // Cancel any scheduled MSAL cleanup
    msalCacheManager.cancelCleanup();

    // Clear user store completely
    userStore.clearSession();

    // Clear storage
    storageService.removeItem(this.TOKEN_KEY);
    await Preferences.remove({ key: this.AUTH_ATTEMPT_KEY });

    // Clear all MSAL cache
    msalCacheManager.clearMSALCache();

    // Also clear localStorage to ensure complete cleanup
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.AUTH_ATTEMPT_KEY);
  }

  private async clearIOSWebViewCache(): Promise<void> {
    if (!this.isIOS) return;

    try {
      // Clear local storage
      localStorage.clear();
      sessionStorage.clear();

      // Clear all preferences
      await Preferences.clear();

      // Note: Cookies are not used in this application
    } catch (error) {
      logger.warn('Error clearing iOS WebView cache:', error);
    }
  }

  private decodeToken(token: string) {
    const parts = token.split('.');
    const payload = JSON.parse(atob(parts[1]));
    return { payload };
  }

  private redirectToHome(): void {
    const userStore = useUserStore();

    // Redirect to appropriate home page based on user role
    const homeRoute = getDefaultRouteForUser(userStore.userRoles);
    router.push(homeRoute);
  }

  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return result;
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }
}

export const authService = new AuthService();
